import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ModalService } from '@app/services/modal/modal.service';
import { ServiceConfigService } from '@app/services/service-config.service';
import { SubmissionStatusService } from '@app/services/submission-status.service';
import { TranslateService } from '@ngx-translate/core';


@Component({
    selector: 'nh-app-status',
    templateUrl: './app-status.component.html',
    styleUrls: ['./app-status.component.scss'],
    standalone: false
})
export class AppStatusComponent implements OnInit {

  isExpanded = false;
  expandedIndex: number | null = null;
  initialStatuses = ['SUBMITTED', 'REVIEW IN PROGRESS', 'PROCESSED']
  appDetails = [];
  translatedLang;
  globalLanguage;
  paginatedData = [];
  pageSize = 5;
  currentPage = 1;
  totalPages = 1;
  visiblePagesLength = 7;
  pages: number[] = [];
  loadingStatusMap: { [key: string]: boolean } = {};
  submissionStatusAllAppsSwitch: boolean = false; // calling new api to fetch status for specific TNumber when it is false
  appSubmittedBy: string;
  constructor(private router: Router, public submissionStatusService: SubmissionStatusService, private titleService: Title, private modalService: ModalService, public translateService: TranslateService, private serverConfig: ServiceConfigService) {
    this.titleService.setTitle('Submission Status');
  }

  ngOnInit(): void {
    this.submissionStatusAllAppsSwitch = this.serverConfig.getServerConfig().SUBMISSION_STATUS_ALL_APPS_SWITCH;
    const currentUser = JSON.parse(sessionStorage.getItem('currentUser'));
    const accountType = currentUser ? currentUser.accountTypeCd : sessionStorage.getItem('userType');
    const indiv = sessionStorage.getItem('individualId');
    this.submissionStatusService.getSubmittedAppDetails(indiv, accountType).subscribe((res: any) => {
      this.appDetails = res.appsStatusData.map(record => ({
        ...record,
        submissionDateOnly: this.extractDate(record.submissionDate)
      }));
      this.loadData();
    })

    this.translatedLang = this.translateService.getDefaultLang();
    this.globalLanguage = sessionStorage.getItem('globalTranslatedLan')
    this.translateService.onDefaultLangChange.subscribe((res) => {
      this.translatedLang = res.lang;
    });
  }

  loadData() {
    this.totalPages = Math.ceil(this.appDetails.length / this.pageSize);
    this.pages = Array.from({ length: this.totalPages }, (_, i) => i + 1);
    this.updatePageWindow();
    this.updatePagination();
  }

  updatePagination() {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.paginatedData = this.appDetails.slice(start, end);
  }

  changePage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePageWindow();
      this.updatePagination();
    }
  }

  extractDate(dateTime: string): string {
    return dateTime.split(' ')[0];
  }

  learnMore(): void {
    this.modalService.openStatusTypes().subscribe();
  }

  toggleExpand(index: number, wpAppNumber: string): void {
    this.expandedIndex = this.expandedIndex === index ? null : index;
    // when false fetch only single status for that specific TNumber
    if (!this.submissionStatusAllAppsSwitch) {
      this.loadingStatusMap[wpAppNumber] = true;
      this.appSubmittedBy = null;
      this.submissionStatusService.getAppStatus(wpAppNumber).subscribe((res: any) => {
        const updatedStatus = res?.appsStatusData[0]?.appTrackstatus;
        this.appSubmittedBy = res?.appsStatusData[0]?.appSubmittedBy;
        if (updatedStatus) {
          this.updateAppStatus(wpAppNumber, updatedStatus)
        }
        this.loadingStatusMap[wpAppNumber] = false;
      },
      );
    }
  }

  // helper function to update the status for the specific record
  updateAppStatus(wpAppNumber: string, status: string): void {
    const updateStatus = (list: any[]) => {
      const target = list.find(item => item?.wpAppnumber?.toString().trim() === wpAppNumber.toString().trim());
      if (target) {
        target.appTrackstatus = status;
      }
    }
    updateStatus(this.appDetails);
    updateStatus(this.paginatedData);
  }

  getStatusClass(status: string, currentStatus: string): string {
    if (currentStatus === 'INFORMATION NEEDED') {
      if (status === 'SUBMITTED') {
        return "past-status";
      } else if (status === 'INFORMATION NEEDED') {
        return "highlighted-red";
      }
      return 'grayed-out'
    } else {
      const order = ['SUBMITTED', 'REVIEW IN PROGRESS', 'PROCESSED'];
      const currentIndex = order.indexOf(currentStatus);
      const statusIndex = order.indexOf(status);
      if (statusIndex < currentIndex) {
        return 'past-status';
      } else if (statusIndex === currentIndex) {
        return 'current-status';
      } else {
        return 'grayed-out'
      }
    }
  }

  getDisplayStatus(status: string): string[] {
    if (status === 'INFORMATION NEEDED') {
      return ['SUBMITTED', 'INFORMATION NEEDED', 'PROCESSED'];
    }
    return this.initialStatuses
  }

  goToDocuments() {
    this.router.navigate(['/user-profile', 'my-documents']);
  }

  goToSubmissions() {
    this.router.navigate(['/user-profile', 'my-submissions']);
  }

  goToDashboard() {
    this.router.navigateByUrl("user-profile/benefit-dashboard");
  }

  goToMyLetters() {
    this.router.navigate(['user-profile/letters/my-letters']);
  }

  getAppTypeLabel(appType: string, format: 'lower' | 'capital' | 'upper' = 'capital'): string {
    if (!appType) return '';

    const normalText = appType.trim().toLowerCase();

    const typeMapping: { [key: string]: string } = {
      rmb: 'renewal',
      rmc: 'change',
      afb: 'application'
    };

    const label = typeMapping[normalText] || normalText;
    switch (format) {
      case 'lower':
        return label.toLowerCase();
      case 'upper':
        return label.toUpperCase();
      case 'capital':
      default:
        return label.charAt(0).toUpperCase() + label.slice(1);
    }
  }

  getProcessedAppTypeLabel(appType: string): string {
    const typeMapping: { [key: string]: string } = {
      RMC: 'change',
      RMB: 'renewal',
      AFB: 'application'
    };
    return typeMapping[appType.trim()] || appType.trim();
  }

  getProcessedAppTypeLabelSP(appType: string): string {
    const typeMapping: { [key: string]: string } = {
      RMC: 'cambio',
      RMB: 'renovación',
      AFB: 'solicitud'
    };
    return typeMapping[appType.trim()] || appType.trim();
  }

  /* Updating the visible page numbers */
  updatePageWindow(): void {
    this.pages = [];
    let start = this.currentPage;
    // Ensuring that if we're near the end, we don't go past totalPages
    if (start + this.visiblePagesLength - 1 > this.totalPages) {
      start = Math.max(1, this.totalPages - this.visiblePagesLength + 1);
    }
    // building the visible page list
    for (let i = 0; i < this.visiblePagesLength; i++) {
      const page = start + i;
      if (page <= this.totalPages) {
        this.pages.push(page);
      }
    }
  }
}
